<template>
  <BaseDialog
    v-model="openDialog"
    :max-width="550"
    persistent
    no-exit
    :title="mode === 'edit' ? $t('gallery.aiEdit') : $t('gallery.aiGenerate')"
    title-icon="fi-rs-magic-wand"
  >
    <template #toolbar>
      <v-btn size="xx-small" icon variant="text" @click="tryClose">
        <MySvg svgMaxWidth="30" svg-max-height="30" icon="close-square" />
      </v-btn>
    </template>
    <template #content>
      <!-- Step Indicator -->
      <div class="step-indicator d-flex justify-center py-4">
        <div class="d-flex align-center">
          <div
            class="step-circle"
            :class="{ active: step === '1', completed: step === '2' }"
          >
            <v-icon v-if="step === '2'" size="small">mdi-check</v-icon>
            <span v-else>1</span>
          </div>
          <div class="step-line" :class="{ active: step === '2' }" />
          <div class="step-circle" :class="{ active: step === '2' }">2</div>
        </div>
      </div>

      <!-- Step Content -->
      <div class="step-content">
        <v-slide-x-transition mode="out-in">
          <v-form
            v-if="step === '1'"
            key="step1"
            ref="formRef"
            v-model="valid"
            class="px-6 pb-6"
            @submit.prevent="handleAction"
          >
            <!-- Edit mode image preview -->
            <div
              v-if="mode === 'edit' && editImageSrc"
              class="text-center mb-4"
            >
              <v-img
                :src="editImageSrc"
                max-height="180"
                class="rounded-lg mx-auto"
                max-width="280"
                cover
              />
              <div class="text-caption text-grey mt-1">
                {{ $t('gallery.editImageHelper') }}
              </div>
            </div>

            <!-- Prompt Input -->
            <v-textarea
              v-model="prompt"
              :rules="[rules.required]"
              :label="
                mode === 'edit'
                  ? $t('gallery.aiEditPromptLabel')
                  : $t('gallery.aiPromptLabel')
              "
              :placeholder="
                mode === 'edit'
                  ? $t('gallery.aiEditPromptPlaceholder')
                  : $t('gallery.aiPromptPlaceholder')
              "
              variant="outlined"
              rows="3"
              counter
              maxlength="500"
              auto-grow
              class="mb-2"
            />

            <!-- Optional Character Upload (only when no image yet) -->
            <div v-if="mode === 'generate' && !imagePreview" class="mb-4">
              <div
                class="character-upload-hint d-flex align-center pa-3 rounded-lg"
                @click="triggerFileInput"
              >
                <v-icon size="20" color="grey-darken-1" class="me-3"
                  >mdi-account-plus-outline</v-icon
                >
                <div class="flex-grow-1">
                  <span class="text-body-2 text-grey-darken-2">{{
                    $t('gallery.addCharacterOptional')
                  }}</span>
                  <span class="text-caption text-grey ms-1">{{
                    $t('gallery.addCharacterHint')
                  }}</span>
                </div>
                <v-icon size="18" color="grey">mdi-chevron-left</v-icon>
              </div>

              <v-file-input
                ref="fileInput"
                v-model="selectedFile"
                accept="image/*"
                class="d-none"
                @change="onFileChange"
              />
            </div>

            <!-- Edit Image Preview (when image is loaded for editing) -->
            <div v-if="mode === 'generate' && imagePreview" class="mb-4">
              <div class="edit-image-preview rounded-lg pa-3">
                <div class="d-flex align-center">
                  <v-img
                    :src="imagePreview"
                    height="80"
                    width="80"
                    class="rounded flex-shrink-0"
                    cover
                  />
                  <div class="ms-3 flex-grow-1">
                    <div class="text-body-2 font-weight-medium mb-1">
                      {{ $t('gallery.editingThisImage') }}
                    </div>
                    <div class="text-caption text-error font-weight-bold mb-1">
                      {{ $t('gallery.editImageWarning') }}
                    </div>
                    <div class="text-caption text-grey">
                      {{ $t('gallery.describeChanges') }}
                    </div>
                  </div>
                  <v-btn
                    icon="mdi-close"
                    size="small"
                    variant="text"
                    color="grey"
                    @click="removeImage"
                  />
                </div>
              </div>
            </div>

            <!-- Credits Info -->
            <div
              class="credits-info rounded-lg pa-3 mb-2 d-flex align-center justify-space-between"
            >
              <div class="d-flex align-center">
                <v-icon color="amber" class="me-2">mdi-lightning-bolt</v-icon>
                <span class="text-body-2"
                  >{{ aiCredits }} {{ $t('generics.points') }}</span
                >
              </div>
              <!-- <v-chip  size="small" color="primary" variant="tonal"
                >{{ $t('generics.cost') }}: 50</v-chip
              > -->
            </div>
            <v-btn
              variant="text"
              color="primary"
              size="small"
              block
              class="mb-2"
              @click="openPaymentDialog = true"
            >
              {{ $t('generics.buyAiCredits') }}
            </v-btn>
            <!-- Generate Button -->
            <div class="text-caption text-medium-emphasis text-center">
              {{ $t('generics.aiDisclaimer') }}
            </div>
            <v-btn
              :loading="loading"
              :disabled="
                !valid || loading || (!isAdmin && Number(aiCredits || 0) < 50)
              "
              class="generate-btn mt-2"
              size="large"
              block
              type="submit"
            >
              <v-icon class="me-2">mdi-sparkles</v-icon>
              {{
                mode === 'edit'
                  ? $t('gallery.editBtn')
                  : $t('gallery.generateBtn')
              }}
            </v-btn>

            <v-btn
              v-if="lastGeneratedUrl"
              variant="text"
              color="secondary"
              block
              class="mt-2"
              @click="step = '2'"
            >
              <v-icon size="18" class="me-1">mdi-arrow-right</v-icon>
              {{ $t('gallery.backToResult') }}
            </v-btn>
          </v-form>

          <!-- Step 2: Result -->
          <div v-else key="step2" class="px-6 pb-6 text-center">
            <div v-if="previousGeneratedUrl" class="d-flex justify-start mb-2">
              <v-btn
                size="x-small"
                variant="tonal"
                color="secondary"
                @click="restorePrevious"
              >
                <v-icon size="14" class="me-1">mdi-undo</v-icon>
                {{ $t('gallery.backToPrevious') }}
              </v-btn>
            </div>
            <v-img
              v-if="lastGeneratedUrl"
              :src="lastGeneratedUrl"
              class="result-image rounded-xl mx-auto mb-5"
              max-width="300"
              aspect-ratio="1"
              cover
            />

            <v-text-field
              v-model="imageTitle"
              :label="$t('gallery.imageTitle')"
              variant="outlined"
              density="comfortable"
              class="mb-5"
              hide-details
            />

            <v-btn
              color="success"
              size="large"
              :loading="saving"
              @click="handleSave"
              block
              class="mb-3 rounded-lg"
            >
              <v-icon class="me-2">mdi-check</v-icon>
              {{ $t('gallery.approveAndSave') }}
            </v-btn>

            <div class="d-flex gap-2">
              <v-btn
                variant="outlined"
                color="primary"
                class="flex-grow-1 rounded-lg"
                @click="editGeneratedImage"
              >
                <v-icon size="18" class="me-1">mdi-image-edit</v-icon>
                {{ $t('gallery.editWithAI') }}
              </v-btn>

              <v-btn
                variant="outlined"
                class="flex-grow-1 rounded-lg"
                @click="tryGoBack"
              >
                {{ $t('Go back') }}
              </v-btn>
            </div>
          </div>
        </v-slide-x-transition>
      </div>

      <!-- Loading Overlay -->
      <v-overlay
        :model-value="loading"
        class="align-center justify-center"
        contained
        persistent
      >
        <div class="text-center">
          <v-progress-circular
            indeterminate
            color="white"
            size="48"
            width="3"
          />
          <p class="mt-3 text-white text-body-1">
            {{
              mode === 'edit'
                ? $t('gallery.editingImage')
                : $t('gallery.generatingImage')
            }}
          </p>
        </div>
      </v-overlay>
    </template>
  </BaseDialog>

  <payment-handler
    v-if="openPaymentDialog"
    v-model="openPaymentDialog"
    type="ai"
  />
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import BaseDialog from '@/components/dialogs/BaseDialog.vue'
import PaymentHandler from '@/components/handler/PaymentHandler.vue'
import { generateAIImage, editAIImage, saveAIImage } from '@/api/mediaApi'
import { useSwal } from '@/composables/useSwal'
import { useI18n } from 'vue-i18n'
import { useMediaStore } from '@/store/media'
import { useAuthStore } from '@/store/auth'
import { storeToRefs } from 'pinia'

const { t } = useI18n()
const { timeoutSwal, createSwal } = useSwal()
const mediaStore = useMediaStore()
const authStore = useAuthStore()

const { aiCredits, isAdmin } = storeToRefs(authStore)

const openPaymentDialog = ref(false)

const props = defineProps({
  mode: {
    type: String,
    default: 'generate', // 'generate' | 'edit'
  },
  media: {
    type: Object,
    default: () => null,
  },
})

const emit = defineEmits(['generated', 'approved'])

const openDialog = defineModel<boolean>('modelValue')

const step = ref('1')
const formRef = ref()
const valid = ref(false)
const loading = ref(false)
const saving = ref(false)
const prompt = ref('')
const imageTitle = ref('')
const strength = ref(0.6)
const selectedFile = ref<File | null>(null)
const imagePreview = ref<string | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)

const lastGeneratedUrl = ref<string | null>(null)
const previousGeneratedUrl = ref<string | null>(null)

const editImageSrc = computed(() => {
  if (imagePreview.value) return imagePreview.value
  if (props && props.media) {
    return props.media.url || props.media.thumbnail || props.media.src || null
  }
  return null
})

watch(openDialog, val => {
  if (!val) {
    step.value = '1'
    prompt.value = ''
    imageTitle.value = ''
    lastGeneratedUrl.value = null
    previousGeneratedUrl.value = null
    imagePreview.value = null
    selectedFile.value = null
  }
})

const rules = {
  required: (v: any) =>
    (typeof v === 'string' ? !!v?.trim() : !!v) ||
    t('errors.validate.required'),
}

function triggerFileInput() {
  fileInput.value?.click()
}

function onFileChange() {
  const file = selectedFile.value
  if (!file) {
    imagePreview.value = null
    return
  }

  const reader = new FileReader()
  reader.onload = e => {
    imagePreview.value = e.target?.result as string
    lastGeneratedUrl.value = null // Clear preview when new file is uploaded
  }
  reader.readAsDataURL(file)
}

async function removeImage() {
  const { isConfirmed } = await createSwal({
    icon: 'warning',
    title: t('gallery.aiLeaveWarning'),
    confirmButtonText: t('generics.yes'),
    cancelButtonText: t('generics.no'),
  })
  if (!isConfirmed) return
  selectedFile.value = null
  imagePreview.value = null
  lastGeneratedUrl.value = null
}

function useAsReference() {
  if (lastGeneratedUrl.value) {
    imagePreview.value = lastGeneratedUrl.value
    // We don't nullify lastGeneratedUrl anymore, so we can go back
    step.value = '1'
  }
}

function editGeneratedImage() {
  if (lastGeneratedUrl.value) {
    imagePreview.value = lastGeneratedUrl.value
    // We don't nullify lastGeneratedUrl anymore, so we can go back
    prompt.value = ''
    step.value = '1'
  }
}

function restorePrevious() {
  if (previousGeneratedUrl.value) {
    const current = lastGeneratedUrl.value
    lastGeneratedUrl.value = previousGeneratedUrl.value
    previousGeneratedUrl.value = current
  }
}

async function tryGoBack() {
  if (lastGeneratedUrl.value) {
    const { isConfirmed } = await createSwal({
      icon: 'warning',
      title: t('gallery.aiLeaveWarning'),
      confirmButtonText: t('generics.yes'),
      cancelButtonText: t('generics.no'),
    })

    if (!isConfirmed) return
  }
  step.value = '1'
}

async function tryClose() {
  if (lastGeneratedUrl.value) {
    const { isConfirmed } = await createSwal({
      icon: 'warning',
      title: t('gallery.aiLeaveWarning'),
      confirmButtonText: t('generics.yes'),
      cancelButtonText: t('generics.no'),
    })

    if (!isConfirmed) return
  }
  openDialog.value = false
}

const handleAction = async () => {
  const { valid: isFormValid } = await formRef.value?.validate()
  if (!isFormValid) return

  try {
    if (!isAdmin.value && Number(aiCredits.value || 0) < 10) {
      openPaymentDialog.value = true
      return
    }

    loading.value = true

    let result
    if (props.mode === 'generate' && !imagePreview.value) {
      result = await generateAIImage({
        prompt: prompt.value,
        previewOnly: true,
      })
    } else {
      // If it's edit mode OR generate mode with an uploaded image
      result = await editAIImage({
        prompt: prompt.value,
        imageId: props.media?._id || null,
        imageStream: imagePreview.value,
        strength: strength.value,
        previewOnly: true,
      })
    }

    if (result && result.url) {
      if (lastGeneratedUrl.value) {
        previousGeneratedUrl.value = lastGeneratedUrl.value
      }
      lastGeneratedUrl.value = result.url
      if (!imageTitle.value) {
        imageTitle.value = prompt.value
      }
      step.value = '2'
      await authStore.checkLogin()
    }
  } catch (error: any) {
    console.error('AI Image Error:', error)
    timeoutSwal({
      icon: 'error',
      title: error.response?.data?.message || t('gallery.aiGenerateError'),
      timer: 4000,
    })
  } finally {
    loading.value = false
  }
}

const handleSave = async () => {
  if (!lastGeneratedUrl.value) return

  emit('approved', {
    url: lastGeneratedUrl.value,
    title: imageTitle.value || prompt.value,
  })

  openDialog.value = false
  prompt.value = ''
  imageTitle.value = ''
  lastGeneratedUrl.value = null
}
</script>

<style scoped lang="scss">
.step-indicator {
  .step-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 500;
    background: #e0e0e0;
    color: #666;
    transition: all 0.3s ease;

    &.active {
      background: rgb(var(--v-theme-primary));
      color: white;
    }

    &.completed {
      background: rgb(var(--v-theme-success));
      color: white;
    }
  }

  .step-line {
    width: 60px;
    height: 2px;
    background: #e0e0e0;
    margin: 0 8px;
    transition: all 0.3s ease;

    &.active {
      background: rgb(var(--v-theme-primary));
    }
  }
}

.character-upload-hint {
  background: #fafafa;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;

  &:hover {
    background: #f5f5f5;
    border-color: #e0e0e0;
  }
}

.character-preview {
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  border: 1px solid #c8e6c9;
}

.edit-image-preview {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border: 1px solid #bbdefb;
}

.credits-info {
  background: #fafafa;
}

.generate-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
}

.result-image {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.gap-2 {
  gap: 8px;
}

:deep(.v-field) {
  border-radius: 10px;
}
</style>
